import imagemIniciativa from 'assets/img/2026/imagem_iniciativa.png';
import { IniciativaFormatada } from "types/iniciativa";
import { clearRegistrationUser, convertSharePointToEmbed, getSharePointVideoThumbnail } from 'utils/fns';
import { dedupParticipantes } from 'utils/participantes-dedup';
import {
  CategoriaPremioId,
  getCategoriaCor,
  getCategoriaLabel,
  isCategoriaElbb,
  normalizeCategoryPremio,
} from 'utils/iniciativa-categoria';
import { api } from "./api";
import { dedupedFetch } from "./cache";
import { aplicarValidacaoEmIniciativas } from './validacao-etapa1-final.service';

export interface BuscarIniciativasParams {
  status?: string;
  userId?: string;
  categoria?: CategoriaPremioId;
  search?: string;
  classificadaEtapa1?: boolean;
}

export interface BuscarIniciativasPaginadasParams extends BuscarIniciativasParams {
  page: number;
  pageSize: number;
}

export interface IniciativasPaginadas {
  items: IniciativaFormatada[];
  total: number;
  page: number;
  pageSize: number;
}

const extractRegistration = (value?: string): string => {
  if (!value) return '';
  return clearRegistrationUser(value);
};

const formatDisplayName = (nome: string, matricula: string, prefixo?: string): string => {
  const nomeFinal = nome || 'Não informado';
  const chave = matricula ? `F${matricula}` : '-';
  const prefixoFinal = prefixo || '-';

  return `${nomeFinal} (${chave} - ${prefixoFinal})`;
};

const normalizePrefix = (prefixo: unknown): string => {
  if (prefixo === null || prefixo === undefined) {
    return '';
  }

  const value = String(prefixo).trim();
  return value;
};

const getPreferredName = (source?: any): string => {
  return source?.extra?.nome_guerra || source?.nome_guerra || source?.nome || source?.name ||  source?.extra?.nome || '';
};

const getCategoriaInfo = (iniciativa: any): { categoria: string; corCategoria: string; categoryPremio: string } => {
  const categoryPremio = normalizeCategoryPremio(
    iniciativa.categoryPremio ||
    iniciativa.CategoryPremio ||
    iniciativa.category ||
    iniciativa.Category ||
    ''
  );

  return {
    categoryPremio,
    categoria: getCategoriaLabel(categoryPremio),
    corCategoria: getCategoriaCor(categoryPremio)
  };
};

export const formatarIniciativa = (iniciativa: any, userId?: string): IniciativaFormatada => {
  const { categoria, corCategoria, categoryPremio } = getCategoriaInfo(iniciativa);

  const titulo = iniciativa.title ||
                 iniciativa.diretoria ||
                 iniciativa.indicator?.perspectiva ||
                 'Iniciativa';

  const tema = iniciativa.indicator?.perspectiva ||
               iniciativa.title ||
               'Tema não informado';

  const descricao = iniciativa.description ||
                    iniciativa.result ||
                    `Iniciativa de ${iniciativa.owner_name_guerra || iniciativa.owner_name || 'Equipe'}`;

  const resultado = String(iniciativa.result || '');

  const pergunta = iniciativa.indicator?.nome
    ? `Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico ${iniciativa.indicator.nome}?`
    : 'Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico?';

  const diretoria = iniciativa.diretoria ||
                    iniciativa.owner_nome_dependencia ||
                    categoria;

  // Formatar data de cadastro/inscrição
  const formatarData = (data: string | number | undefined) => {
    if (!data) return '';
    const date = new Date(data as any);
    if (isNaN(date.getTime())) return '';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  // Participantes do time (se existir)
  const confirmedPlayers: string[] = iniciativa.confirmedPlayers || [];

  // Normaliza IDs para comparação (remove prefixo F e zeros à esquerda)
  const isConfirmed = (id: string) =>
    confirmedPlayers.some(cp => clearRegistrationUser(cp) === clearRegistrationUser(id));

  const targetById = new Map<string, any>();
  (iniciativa.target || []).forEach((targetMember: any) => {
    const targetId = extractRegistration(targetMember?._id || targetMember?.extra?.id);
    if (targetId) {
      targetById.set(targetId, targetMember);
    }
  });

  const ownerMatricula = extractRegistration(iniciativa.owner);
  const ownerTarget = targetById.get(ownerMatricula);
  const ownerName =
    getPreferredName(ownerTarget) ||
    iniciativa.owner_name_guerra ||
    iniciativa.owner_name ||
    'Responsável';
  const ownerPrefixo = normalizePrefix(
    ownerTarget?.extra?.prefixo || iniciativa.prefixo || iniciativa.original_manager_detail?.prefixo
  );
  const ownerDisplay = formatDisplayName(ownerName, ownerMatricula, ownerPrefixo);

  const participantes: Array<{
    nome: string;
    matricula: string;
    prefixo?: string;
    displayName?: string;
    confirmado: boolean;
    isOwner?: boolean;
  }> = dedupParticipantes(iniciativa.team).map((membro: any) => {
    const matricula = extractRegistration(membro?._id || membro?.extra?.id);
    const targetMember = targetById.get(matricula);
    const nome = getPreferredName(targetMember) || getPreferredName(membro) || 'Participante';
    const prefixo = normalizePrefix(targetMember?.extra?.prefixo || membro?.extra?.prefixo);
    const ownerMatch = !!ownerMatricula && clearRegistrationUser(matricula) === clearRegistrationUser(ownerMatricula);

    return {
      nome,
      matricula,
      prefixo,
      displayName: formatDisplayName(nome, matricula, prefixo),
      confirmado: isConfirmed(matricula),
      isOwner: ownerMatch,
    };
  });

  // Adiciona o owner se não estiver no time
  const ownerJaNoTime = participantes.some(
    p => clearRegistrationUser(p.matricula) === clearRegistrationUser(ownerMatricula || '')
  );
  if (!ownerJaNoTime && ownerMatricula) {
    participantes.unshift({
      nome: ownerName,
      matricula: ownerMatricula,
      prefixo: ownerPrefixo,
      displayName: ownerDisplay,
      confirmado: isConfirmed(ownerMatricula),
      isOwner: true,
    });
  }

  const autores = participantes.map((participante) => participante.displayName || participante.nome).join(', ') || ownerDisplay;

  // Processar estatísticas do campo log
  const log = iniciativa.log || [];
  const elogios = log.filter((l: any) => l.actionId === 'elogiar');
  const compartilhamentos = log.filter((l: any) => l.actionId === 'compartilhar');
  const favoritos = log.filter((l: any) => l.actionId === 'favoritar');

  // Considera apenas o último elogio por usuário (maior time)
  const elogiosPorUsuario = new Map<string, { time: number; index: number; elogio?: string }>();
  elogios.forEach((l: any, index: number) => {
    const logUserId = l.userId;
    if (!logUserId) {
      return;
    }

    const time = Number(l.time) || 0;
    const elogio = l.attributes?.elogio;
    const registroAtual = elogiosPorUsuario.get(logUserId);

    if (!registroAtual || time > registroAtual.time || (time === registroAtual.time && index > registroAtual.index)) {
      elogiosPorUsuario.set(logUserId, {
        time,
        index,
        elogio
      });
    }
  });

  const elogiosEfetivos = Array.from(elogiosPorUsuario.values()).filter((item) => Boolean(item.elogio));

  // Contar elogios por tipo (baseado nos elogios efetivos)
  const elogiosMap = new Map<string, number>();
  elogiosEfetivos.forEach((item) => {
    const tipoElogio = item.elogio;
    if (tipoElogio) {
      elogiosMap.set(tipoElogio, (elogiosMap.get(tipoElogio) || 0) + 1);
    }
  });

  const elogiosPorTipo = Array.from(elogiosMap.entries()).map(([elogio, quantidade]) => ({
    elogio,
    quantidade
  }));

  // Contar favoritos ativos (filtrar pelo último registro de cada usuário com ativo=true)
  const favoritosPorUsuario = new Map<string, { time: number; ativo: boolean }>();
  favoritos.forEach((l: any) => {
    const userId = l.userId;
    const time = l.time || 0;
    const ativo = l.attributes?.ativo !== false; // Default true se não existir

    if (!favoritosPorUsuario.has(userId) || time > (favoritosPorUsuario.get(userId)?.time || 0)) {
      favoritosPorUsuario.set(userId, { time, ativo });
    }
  });

  // Contar apenas usuários com ativo = true (favoritado)
  const totalFavoritos = Array.from(favoritosPorUsuario.values()).filter(f => f.ativo === true).length;

  // Verificar ações do usuário atual
  let usuarioElogio: string | undefined;
  let usuarioFavoritou = false;
  let usuarioCompartilhou = false;

  if (userId) {
    // Buscar último elogio efetivo do usuário
    usuarioElogio = elogiosPorUsuario.get(userId)?.elogio;

    // Verificar se usuário favoritou (último registro com ativo=true)
    const favoritosUsuario = favoritos.filter((l: any) => l.userId === userId);
    if (favoritosUsuario.length > 0) {
      const ultimoFavorito = favoritosUsuario.sort((a: any, b: any) => (b.time || 0) - (a.time || 0))[0];
      usuarioFavoritou = ultimoFavorito.attributes?.ativo !== false; // Default true se não existir
    }

    // Verificar se usuário compartilhou
    usuarioCompartilhou = compartilhamentos.some((l: any) => l.userId === userId);
  }

  return {
    iniciativaOriginal: iniciativa,
    id: iniciativa._id,
    titulo,
    tema,
    descricao,
    resultado,
    pergunta,
    texto: iniciativa.description || iniciativa.result || descricao,
    autores,
    categoria,
    categoryPremio,
    corCategoria,
    imagem: getSharePointVideoThumbnail(iniciativa.documento?.find((d: any) => d.type === 'video')?.file) || iniciativa.owner_avatar?.image?.small?.url || imagemIniciativa,
    status: iniciativa.status,
    diretoria,
    cadastradoPor: ownerName,
    cadastradoMatricula: ownerMatricula,
    cadastradoPrefixo: ownerPrefixo,
    cadastradoDisplay: ownerDisplay,
    cadastradoData: formatarData(iniciativa.created || iniciativa.createdAt),
    inscritoPor: iniciativa.status === 'inscrita'
      ? (iniciativa.inscricao_player || '--')
      : '--',
    inscritoMatricula: '',
    inscritoPrefixo: '',
    inscritoDisplay: iniciativa.status === 'inscrita'
      ? (iniciativa.inscricao_player || '--')
      : '--',
    inscritoData: iniciativa.status === 'inscrita' && iniciativa.inscricao_player
      ? formatarData(iniciativa.dt_inscricao || iniciativa.updatedAt)
      : '',
    participantes,
    documentacao: iniciativa.documento?.find((d: any) => d.type === 'file' && d.file)?.file,
    videoUrl: (() => {
      const doc = iniciativa.documento?.find((d: any) => d.type === 'video');
      if (!doc) return undefined;
      return doc.embedUrl || convertSharePointToEmbed(doc.file) || doc.file;
    })(),
    originalVideoUrl: iniciativa.documento?.find((d: any) => d.type === 'video')?.file,
    direcionadorEstrategico: iniciativa.indicator?.perspectiva || categoryPremio || diretoria,
    totalElogios: elogiosEfetivos.length,
    totalCompartilhamentos: compartilhamentos.length,
    totalFavoritos,
    elogiosPorTipo,
    usuarioElogio,
    usuarioFavoritou,
    usuarioCompartilhou,
    classificadaEtapa1: iniciativa.classificada_etapa_1 === true,
    classificadaEtapa2: iniciativa.classificadaEtapa2 === true,
    position: typeof iniciativa.position === 'number' ? iniciativa.position : undefined,
  };
};

export const buscarIniciativas = async (params?: BuscarIniciativasParams): Promise<IniciativaFormatada[]> => {
  try {
    const body: Record<string, any> = {};
    if (params?.categoria) body.categoria = params.categoria;
    if (params?.status) body.status = params.status;

    const { data } = await api.post("/find/tasks_etapa1", body);

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return aplicarValidacaoEmIniciativas(
      data.map((ini: any) => formatarIniciativa(ini, params?.userId))
    );
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativasEtapa2 = async (
  category: string,
  userId?: string
): Promise<IniciativaFormatada[]> => {
  return dedupedFetch(
    `iniciativas-etapa2-${category}`,
    async () => {
      try {
        const body: Record<string, any> = { category };
        const { data } = await api.post('/find/tasks_etapa2', body,       {
        headers: { Range: `items=0-500` },
      });
        if (!data || !Array.isArray(data)) {
          return [];
        }

        return aplicarValidacaoEmIniciativas(
          data.map((ini: any) => formatarIniciativa(ini, userId))
        );
      } catch (error) {
        console.error('Erro ao buscar iniciativas etapa 2:', error);
        throw error;
      }
    },
    60_000
  );
};

export const buscarIniciativasEtapa3 = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-etapa3-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/tasks_etapa3', {});
      if (!data || !Array.isArray(data)) return [];
      return aplicarValidacaoEmIniciativas(
        data
          .filter((ini: any) => (ini.categoryPremio || '').toUpperCase() !== 'ELBB')
          .map((ini: any) => formatarIniciativa(ini, userId))
      );
    },
    60_000
  );

export const buscarIniciativasVencedoras = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-vencedoras-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/resultado_final', {});
      if (!data || !Array.isArray(data)) return [];
      return aplicarValidacaoEmIniciativas(
        data
          .filter((ini: any) => (ini.categoryPremio || '').toUpperCase() !== 'ELBB')
          .map((ini: any) => formatarIniciativa(ini, userId))
      );
    },
    60_000
  );

/**
 * Vencedoras ELBB, como no main-elbb (sem validação de etapa 1).
 * O `/find/resultado_final_elbb` já filtra a categoria no servidor.
 */
export const buscarIniciativasVencedorasElbb = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-vencedoras-elbb-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/resultado_final_elbb', {});
      if (!Array.isArray(data)) return [];
      return data.map((ini: any) => formatarIniciativa(ini, userId));
    },
    60_000
  );

export const buscarIniciativasEtapa3Elbb = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-etapa3-elbb-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/tasks_etapa3', {});
      if (!data || !Array.isArray(data)) return [];
      return aplicarValidacaoEmIniciativas(
        data
          .filter((ini: any) => isCategoriaElbb(ini.categoryPremio))
          .map((ini: any) => formatarIniciativa(ini, userId))
      );
    },
    60_000
  );

export const buscarIniciativasPaginadas = async (
  params: BuscarIniciativasPaginadasParams
): Promise<IniciativasPaginadas> => {
  const fetchFn = async () => {
    try {
      const body: Record<string, any> = {
        page: params.page,
        pageSize: params.pageSize,
      };
      if (params.categoria) body.categoria = params.categoria;
      if (params.search?.trim()) body.search = params.search.trim();
      if (params.status) body.status = params.status;
      if (params.classificadaEtapa1 !== undefined) body.classificadaEtapa1 = params.classificadaEtapa1;

      const { data } = await api.post("/find/tasks_etapa1", body);

      // Com paginação o aggregate retorna [{ items: [...], total: N }]
      const raw = Array.isArray(data) ? data[0] : data;
      const items = Array.isArray(raw?.items) ? raw.items : [];
      const total = Number(raw?.total) || 0;

      return {
        items: await aplicarValidacaoEmIniciativas<IniciativaFormatada>(
          items.map((ini: any) => formatarIniciativa(ini, params.userId))
        ),
        total,
        page: params.page,
        pageSize: params.pageSize,
      };
    } catch (error: any) {
      throw error;
    }
  };

  const isCacheable = !params.search && params.page === 1;
  if (isCacheable) {
    const classificadaKey = params.classificadaEtapa1 === undefined
      ? 'all'
      : String(params.classificadaEtapa1);
    return dedupedFetch(
      `iniciativas-paginadas-${params.categoria || 'todas'}-${classificadaKey}`,
      fetchFn,
      60_000
    );
  }

  return fetchFn();
};

export const buscarIniciativaPorId = async (
  id: string,
  userId?: string,
  categoria?: CategoriaPremioId
): Promise<IniciativaFormatada | null> => {
  try {
    const body: Record<string, any> = { _id: id };
    if (categoria) body.categoria = categoria;

    const { data } = await api.post("/find/tasks_etapa1", body);

    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const [enriquecida] = await aplicarValidacaoEmIniciativas([
      formatarIniciativa(data[0], userId),
    ]);
    return enriquecida;
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativaPorPlayer = async (
  _id: string,
  userId?: string,
  categoria?: CategoriaPremioId
): Promise<IniciativaFormatada | null> => {
  try {
    const body: Record<string, any> = { player: userId };
    if (categoria) body.categoria = categoria;

    const { data } = await api.post("/find/tasks_etapa1", body);

    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const [enriquecida] = await aplicarValidacaoEmIniciativas([
      formatarIniciativa(data[0], userId),
    ]);
    return enriquecida;
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativasPorPlayer = async (
  userId: string,
  categoria?: CategoriaPremioId
): Promise<IniciativaFormatada[]> => {
  try {
    const body: Record<string, any> = { player: userId };
    if (categoria) body.categoria = categoria;

    const { data } = await api.post("/find/tasks_etapa1", body);

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return aplicarValidacaoEmIniciativas(
      data.map((ini: any) => formatarIniciativa(ini, userId))
    );
  } catch (error: any) {
    throw error;
  }
};

/**
 * Busca as iniciativas favoritadas pelo usuário
 * Filtra o log de ações do usuário para encontrar favoritos ativos (value = 1)
 */
export const buscarIniciativasFavoritas = async (
  userId: string,
  categoria?: CategoriaPremioId
): Promise<IniciativaFormatada[]> => {
  try {
    const todasIniciativas = await buscarIniciativas({ userId, categoria });
    return todasIniciativas.filter(iniciativa => iniciativa.usuarioFavoritou);
  } catch (error: any) {
    throw error;
  }
};
